import { useEffect, useMemo, useState } from 'react';

import {
  Alert,
  Box,
  Button,
  Card,
  Group,
  Loader,
  Modal,
  Select,
  Stack,
  Switch,
  Text,
  TextInput
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { IconEye } from '@tabler/icons-react';

import { useAppSelector } from '@/store/hooks';
import {
  buildPrintLetterValues,
  fillPrintLetter,
  formatPrintLetterDate,
  printLettersSaveData
} from '@/utils/printLetter';
import PrintLetterPreview from '@/components/PrintLetterPreview';

import {
  loadPrintLetterTemplate,
  loadPrintLettersRecord,
  printLetterErrorMessage,
  savePrintLettersRecord
} from '@/services/printLetters';

import type { PrintLetter, PrintLettersRecord } from '@/types/printLetter';

import { letterClinics } from './letterTemplates';

type LetterDraft = Pick<PrintLetter, 'id' | 'name' | 'isEnabled'>;

interface LoadedRecord {
  organizationId: string;
  source: 'clinic' | 'account';
  record: PrintLettersRecord;
  raw: Record<string, unknown>;
}

interface PreviewState {
  name: string;
  html: string;
}

function draftsFrom(record: PrintLettersRecord | null | undefined): LetterDraft[] {
  return (record?.letters ?? []).map(({ id, name, isEnabled }) => ({ id, name, isEnabled }));
}

function sampleDates(today = new Date()): string[] {
  return [30, 120, 210, 300].map((days) => {
    const date = new Date(today);
    date.setDate(date.getDate() + days);
    return formatPrintLetterDate(date);
  });
}

/**
 * Clinic print letters (PostGrid designs printed from the patient page). Admins can rename letters
 * and turn them on or off. Adding a letter or changing its template is done by Rhythm.
 */
const PrintLettersSection = () => {
  const user = useAppSelector((state) => state.auth.user);
  const organizations = user?.organizations;
  const clinics = letterClinics(organizations);
  const [clinicId, setClinicId] = useState(clinics[0]?.id ?? '');
  const [loaded, setLoaded] = useState<LoadedRecord | null>(null);
  const [drafts, setDrafts] = useState<LetterDraft[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [previewingId, setPreviewingId] = useState<string | null>(null);
  const [preview, setPreview] = useState<PreviewState | null>(null);

  const activeClinicId = clinics.some((clinic) => clinic.id === clinicId)
    ? clinicId
    : (clinics[0]?.id ?? '');
  const activeClinic = clinics.find((clinic) => clinic.id === activeClinicId);
  const parentId = useMemo(() => {
    const org = (organizations ?? []).find((item) => item?.id === activeClinicId);
    return org?.parent?.id || org?.parentId || '';
  }, [organizations, activeClinicId]);

  useEffect(() => {
    if (!activeClinicId) return;
    let cancelled = false;
    setLoading(true);
    setLoaded(null);
    setDrafts([]);
    const load = async (): Promise<LoadedRecord | null> => {
      const own = await loadPrintLettersRecord(activeClinicId);
      if (own) return { organizationId: activeClinicId, source: 'clinic', ...own };
      if (!parentId || parentId === activeClinicId) return null;
      const account = await loadPrintLettersRecord(parentId).catch(() => null);
      return account ? { organizationId: parentId, source: 'account', ...account } : null;
    };
    void load()
      .then((next) => {
        if (cancelled) return;
        setLoaded(next);
        setDrafts(draftsFrom(next?.record));
      })
      .catch((error: unknown) => {
        if (cancelled) return;
        notifications.show({
          title: 'Could not load print letters',
          message: error instanceof Error ? error.message : 'Try again.',
          color: 'red'
        });
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [activeClinicId, parentId]);

  const editable = loaded?.source === 'clinic';
  const dirty = editable && JSON.stringify(drafts) !== JSON.stringify(draftsFrom(loaded?.record));

  const updateDraft = (id: string, change: Partial<LetterDraft>) =>
    setDrafts((current) =>
      current.map((draft) => (draft.id === id ? { ...draft, ...change } : draft))
    );

  const save = async () => {
    if (!loaded || !editable) return;
    setSaving(true);
    try {
      const data = printLettersSaveData(loaded.raw, drafts, user?.email ?? '');
      await savePrintLettersRecord(loaded.organizationId, data);
      const record: PrintLettersRecord = {
        ...loaded.record,
        letters: loaded.record.letters.map((letter) => {
          const draft = drafts.find((item) => item.id === letter.id);
          return draft
            ? { ...letter, name: draft.name.trim() || letter.name, isEnabled: draft.isEnabled }
            : letter;
        })
      };
      setLoaded({ ...loaded, record, raw: data });
      setDrafts(draftsFrom(record));
      notifications.show({ title: 'Success', message: 'Print letters saved.', color: 'green' });
    } catch (error) {
      notifications.show({
        title: 'Could not save print letters',
        message: error instanceof Error ? error.message : 'Try again.',
        color: 'red'
      });
    } finally {
      setSaving(false);
    }
  };

  const openPreview = async (letter: PrintLetter) => {
    if (!loaded) return;
    setPreviewingId(letter.id);
    try {
      const template = await loadPrintLetterTemplate(loaded.organizationId, letter.id);
      const values = buildPrintLetterValues({
        patient: {
          givenName: 'Jane',
          familyName: 'Sample',
          address: { line1: '123 Main St', city: 'Mesa', state: 'AZ', postalCode: '85201' },
          clinic: { name: activeClinic?.name ?? '' }
        },
        scheduleDates: sampleDates(),
        note: 'Sample patient note.'
      });
      setPreview({ name: letter.name, html: fillPrintLetter(template.html, values) });
    } catch (error) {
      notifications.show({
        title: 'Could not preview the letter',
        message: letter.isEnabled
          ? printLetterErrorMessage(error)
          : 'Turn this letter on and save to preview it.',
        color: 'red'
      });
    } finally {
      setPreviewingId(null);
    }
  };

  const letters = loaded?.record.letters ?? [];

  return (
    <Box>
      <Text fw={700} size="lg" mb={4}>
        Print letters
      </Text>
      <Text size="sm" c="dimmed" mb="md">
        Letters your staff print from a patient&apos;s schedule. Rhythm designs them in PostGrid;
        you can rename them and turn them on or off.
      </Text>

      {clinics.length === 0 ? (
        <Alert color="gray" variant="light">
          No clinic is assigned to this account.
        </Alert>
      ) : (
        <Stack gap="md">
          <Select
            maw={420}
            label="Clinic"
            data={clinics.map((clinic) => ({ value: clinic.id, label: clinic.name }))}
            value={activeClinicId}
            onChange={(value) => value && setClinicId(value)}
            allowDeselect={false}
          />

          {loading ? (
            <Loader size="sm" />
          ) : !loaded || letters.length === 0 ? (
            <Alert color="gray" variant="light">
              No print letter is set up for this clinic. Contact Rhythm to add a print letter.
            </Alert>
          ) : (
            <>
              {!loaded.record.isEnabled && (
                <Alert color="yellow" variant="light">
                  Print letters are turned off for this clinic. Contact Rhythm to turn them on.
                </Alert>
              )}
              {!editable && (
                <Alert color="blue" variant="light">
                  These letters are set up for the whole account. Contact Rhythm to change them.
                </Alert>
              )}

              {letters.map((letter) => {
                const draft = drafts.find((item) => item.id === letter.id);
                return (
                  <Card key={letter.id} withBorder padding="md">
                    <Group justify="space-between" align="flex-end" wrap="nowrap">
                      <TextInput
                        label="Name"
                        flex={1}
                        maw={420}
                        value={draft?.name ?? letter.name}
                        onChange={(event) =>
                          updateDraft(letter.id, { name: event.currentTarget.value })
                        }
                        disabled={!editable || saving}
                      />
                      <Group gap="md" wrap="nowrap">
                        <Switch
                          label="On"
                          color="plum"
                          checked={draft?.isEnabled ?? letter.isEnabled}
                          onChange={(event) =>
                            updateDraft(letter.id, { isEnabled: event.currentTarget.checked })
                          }
                          disabled={!editable || saving}
                        />
                        <Button
                          size="compact-sm"
                          variant="light"
                          color="plum"
                          leftSection={<IconEye size={14} />}
                          loading={previewingId === letter.id}
                          onClick={() => {
                            void openPreview(letter);
                          }}>
                          Preview
                        </Button>
                      </Group>
                    </Group>
                  </Card>
                );
              })}

              <Text size="xs" c="dimmed">
                Contact Rhythm to add a print letter or change its design.
              </Text>

              {editable && (
                <Group>
                  <Button
                    color="plum"
                    onClick={() => {
                      void save();
                    }}
                    loading={saving}
                    disabled={!dirty}>
                    Save
                  </Button>
                  <Button
                    variant="default"
                    onClick={() => setDrafts(draftsFrom(loaded.record))}
                    disabled={!dirty || saving}>
                    Discard changes
                  </Button>
                </Group>
              )}
            </>
          )}
        </Stack>
      )}

      <Modal
        opened={preview !== null}
        onClose={() => setPreview(null)}
        size="900px"
        title={<Text fw={700}>Preview — {preview?.name}</Text>}>
        {preview && (
          <Stack gap="xs">
            <Text size="xs" c="dimmed">
              Shown with sample patient details.
            </Text>
            <PrintLetterPreview html={preview.html} title={`${preview.name} preview`} />
          </Stack>
        )}
      </Modal>
    </Box>
  );
};

export default PrintLettersSection;
